/**
 * Per-request answer to a single question, resolved by Bifrost before Statsig is initialised:
 * *will the post-init reader of `SiteInformationQuery` route it to Relay?*
 *
 * ## Why this exists
 *
 * `isRelayEnabled` resolves the rollout configuration through the Statsig client. On the Bifrost
 * early-preload path, `getSessionData()` (and so `SiteInformationQuery` via `loadSessionData` →
 * `queryInterop`) runs while the pipeline that calls `initializeClientFromValues` is still awaiting
 * `getRenderContext()`. Reading an experiment before then does not fail loudly:
 * `FeatureGates.getExperiment` throws `assertInitialized`, the throw is swallowed, an empty config
 * comes back, and every read yields its default. For the migration experiment that default is
 * `false`, so the write silently goes to Apollo no matter how the rollout is configured.
 *
 * `SPAViewContextProvider` then reads the same query during `renderToString`, after Statsig is
 * available, resolves to Relay, and finds nothing — `frontCoverState` falls back to `''` and
 * `CompanyHubRoute` redirects to Home.
 *
 * Bifrost can answer the question much earlier from the native edge SDK, which needs only the
 * request-derived user context and no network round-trip. `loadSessionData` passes the answer to
 * `queryInterop` as `forceRelay`, so the write lands in whichever cache the read will consult.
 *
 * ## Invariants
 *
 * 1. **It describes the reader, not the writer.** The point is agreement. Forcing the write to
 *    Relay while the reader resolves to Apollo would simply invert the same bug, so Bifrost
 *    evaluates `spa-view-context`'s enrolment — not `session-data`'s.
 * 2. **Set unconditionally, once per request.** Bifrost is a long-lived process and this is module
 *    state, so a request that skipped setting it would read the *previous* request's answer — a
 *    per-tenant hazard. Callers must set it on every request, including error paths, passing `null`
 *    when it cannot be resolved so the normal `isRelayEnabled` path is used instead.
 * 3. **Only `loadSessionData` consumes it.** Deliberately not consulted inside `queryInterop`
 *    itself: that would apply it to every imperative query rather than the one whose routing is
 *    known to be broken.
 */
let ssrForcedRelay: boolean | null = null;

/**
 * Record whether `SiteInformationQuery` should be forced onto Relay for this request. `null` means
 * "no answer — use the normal `isRelayEnabled` path". See the invariants above.
 */
export function setSSRForcedRelay(value: boolean | null): void {
	ssrForcedRelay = value;
}

/** The answer for this request, or `null` when nothing has been recorded. */
export function getSSRForcedRelay(): boolean | null {
	return ssrForcedRelay;
}

export function __TEST_ONLY_resetSSRForcedRelay(): void {
	ssrForcedRelay = null;
}
